import { ONBOARDING_TOTAL_STEPS } from '@perdiem/trpc/onboarding';
import { trackEvent, type AnalyticsScalar } from './analytics';

type ProductEventInput = {
  eventName: string;
  route?: string | null;
  screen?: string | null;
  component?: string | null;
  message?: string | null;
  stepNumber?: number | null;
  stepLabel?: string | null;
  metadata?: Record<string, AnalyticsScalar> | null;
};

type OnboardingStepCompletionInput = {
  route: string | null | undefined;
  stepNumber: number;
  nextStepNumber?: number;
  completionKind: 'continue' | 'skip' | 'auto_advance';
  metadata?: Record<string, AnalyticsScalar> | null;
};

type FireCalculationBaseInput = {
  route: string | null | undefined;
  screen: 'onboarding' | 'fire-plan';
  source: 'onboarding' | 'fire_plan';
  transport: 'subscription' | 'recalc_queue';
  metadata?: Record<string, AnalyticsScalar> | null;
};

type FireCalculationStageInput = FireCalculationBaseInput & {
  status:
    | 'queued'
    | 'starting'
    | 'precalculating'
    | 'running'
    | 'simulating'
    | 'saving';
  progress?: number | null;
};

type FireCalculationOutcomeInput = FireCalculationBaseInput & {
  durationMs?: number | null;
  fiYear?: number | null;
  fiAge?: number | null;
  errorMessage?: string | null;
};

export type SubscriptionConversionEventName =
  | 'subscription.paywall_opened'
  | 'subscription.paywall_packages_loaded'
  | 'subscription.paywall_packages_failed'
  | 'subscription.paywall_plan_selected'
  | 'subscription.checkout_started'
  | 'subscription.checkout_canceled'
  | 'subscription.checkout_failed'
  | 'subscription.purchase_completed';

type SubscriptionConversionEventInput = {
  eventName: SubscriptionConversionEventName;
  trigger: 'onboarding_trial_gate' | 'restricted_access' | 'subscribe_deep_link' | 'settings';
  accessKind?: string | null;
  isAccessRestricted: boolean;
  isPromoTrial: boolean;
  packageId?: string | null;
  packageType?: string | null;
  packageCount?: number | null;
  accountCount?: number | null;
  selectionSource?: 'default' | 'user' | null;
  failureStage?: string | null;
  errorBucket?: string | null;
};

export const ONBOARDING_STEP_LABELS = [
  'About You',
  'Connect Accounts',
  'Verify Transactions',
  'Verify Income',
  'Budget Review',
  'Household',
  'Life Events & FIRE Assumptions',
  'Calculating',
] as const;

export function getOnboardingStepLabel(stepNumber: number): string {
  return ONBOARDING_STEP_LABELS[stepNumber - 1] ?? `Step ${stepNumber}`;
}

export function trackProductEvent(input: ProductEventInput) {
  trackEvent({
    eventName: input.eventName,
    route: input.route,
    screen: input.screen,
    component: input.component,
    message: input.message,
    stepNumber: input.stepNumber,
    stepLabel: input.stepLabel,
    metadata: input.metadata,
  });
}

export function trackSubscriptionConversionEvent(
  input: SubscriptionConversionEventInput,
) {
  const isOnboardingGate = input.trigger === 'onboarding_trial_gate';
  trackProductEvent({
    eventName: input.eventName,
    route: isOnboardingGate ? '/(onboarding)' : '/(app)/settings',
    screen: isOnboardingGate ? 'onboarding' : 'settings',
    component: isOnboardingGate ? 'TrialActivationGate' : 'SubscriptionPaywall',
    message: input.eventName,
    metadata: {
      trigger: input.trigger,
      accessKind: input.accessKind ?? 'unknown',
      isAccessRestricted: input.isAccessRestricted,
      isPromoTrial: input.isPromoTrial,
      packageId: input.packageId ?? null,
      packageType: input.packageType ?? null,
      packageCount: input.packageCount ?? null,
      accountCount: input.accountCount ?? null,
      selectionSource: input.selectionSource ?? null,
      failureStage: input.failureStage ?? null,
      errorBucket: input.errorBucket ?? null,
    },
  });
}

export function trackOnboardingStepCompleted(input: OnboardingStepCompletionInput) {
  trackProductEvent({
    eventName: 'onboarding.step_completed',
    route: input.route,
    screen: 'onboarding',
    component: 'OnboardingFlow',
    message: `Completed ${getOnboardingStepLabel(input.stepNumber)}`,
    stepNumber: input.stepNumber,
    stepLabel: getOnboardingStepLabel(input.stepNumber),
    metadata: {
      completionKind: input.completionKind,
      nextStepNumber: input.nextStepNumber ?? null,
      nextStepLabel:
        input.nextStepNumber != null
          ? getOnboardingStepLabel(input.nextStepNumber)
          : null,
      ...(input.metadata ?? {}),
    },
  });
}

export function trackOnboardingCompleted(params: {
  route: string | null | undefined;
  metadata?: Record<string, AnalyticsScalar> | null;
}) {
  trackProductEvent({
    eventName: 'onboarding.completed',
    route: params.route,
    screen: 'onboarding',
    component: 'OnboardingFlow',
    message: 'Completed onboarding',
    stepNumber: ONBOARDING_TOTAL_STEPS,
    stepLabel: getOnboardingStepLabel(ONBOARDING_TOTAL_STEPS),
    metadata: params.metadata,
  });
}

export function trackAppSessionStarted(params: {
  route: string | null | undefined;
  metadata?: Record<string, AnalyticsScalar> | null;
}) {
  trackProductEvent({
    eventName: 'app.session_started',
    route: params.route,
    screen: 'app',
    component: 'RootLayout',
    message: 'Authenticated session started',
    metadata: params.metadata,
  });
}

export function trackAppInstallFirstSeen(params: {
  route: string | null | undefined;
  metadata?: Record<string, AnalyticsScalar> | null;
}) {
  trackProductEvent({
    eventName: 'app.install_first_seen',
    route: params.route,
    screen: 'app',
    component: 'RootLayout',
    message: 'First session seen on this device',
    metadata: params.metadata,
  });
}

export function trackAppScreenViewed(params: {
  route: string | null | undefined;
  screenName: string;
  metadata?: Record<string, AnalyticsScalar> | null;
}) {
  trackProductEvent({
    eventName: 'app.screen_viewed',
    route: params.route,
    screen: 'app',
    component: 'AppLayout',
    message: params.screenName,
    metadata: {
      screenName: params.screenName,
      ...(params.metadata ?? {}),
    },
  });
}

export function trackAppBootstrapReady(params: {
  route: string | null | undefined;
  durationMs: number;
  metadata?: Record<string, AnalyticsScalar> | null;
}) {
  trackProductEvent({
    eventName: 'app.bootstrap_ready',
    route: params.route,
    screen: 'app',
    component: 'AppLayout',
    message: 'Authenticated app shell ready',
    metadata: {
      durationMs: params.durationMs,
      ...(params.metadata ?? {}),
    },
  });
}

export function trackAppScreenReady(params: {
  route: string | null | undefined;
  screenName: string;
  durationMs: number;
  metadata?: Record<string, AnalyticsScalar> | null;
}) {
  trackProductEvent({
    eventName: 'app.screen_ready',
    route: params.route,
    screen: 'app',
    component: params.screenName,
    message: `${params.screenName} ready`,
    metadata: {
      screenName: params.screenName,
      durationMs: params.durationMs,
      ...(params.metadata ?? {}),
    },
  });
}

export function trackAuthScreenViewed(params: {
  route: string | null | undefined;
  mode: 'signin' | 'signup';
  metadata?: Record<string, AnalyticsScalar> | null;
}) {
  trackProductEvent({
    eventName: 'auth.screen_viewed',
    route: params.route,
    screen: 'auth',
    component: 'LoginScreen',
    message: params.mode === 'signin' ? 'Sign in screen' : 'Sign up screen',
    metadata: {
      intent: params.mode,
      ...(params.metadata ?? {}),
    },
  });
}

type AuthAttemptInput = {
  route: string | null | undefined;
  intent: 'signin' | 'signup';
  provider: 'email' | 'google' | 'apple';
  flow: 'password' | 'oauth_web' | 'oauth_native' | 'id_token_native';
  metadata?: Record<string, AnalyticsScalar> | null;
};

type AuthUserCreatedInput = {
  route: string | null | undefined;
  attemptedIntent: 'signin' | 'signup';
  provider: 'email' | 'google' | 'apple';
  flow: 'password' | 'oauth_web' | 'oauth_native' | 'id_token_native';
  supabaseUserId?: string | null;
  metadata?: Record<string, AnalyticsScalar> | null;
};

export function trackAuthAttemptStarted(params: AuthAttemptInput) {
  trackProductEvent({
    eventName: 'auth.attempt_started',
    route: params.route,
    screen: 'auth',
    component: 'LoginScreen',
    message: `${params.provider} ${params.intent} started`,
    metadata: {
      intent: params.intent,
      provider: params.provider,
      flow: params.flow,
      ...(params.metadata ?? {}),
    },
  });
}

export function trackAuthAttemptSucceeded(params: AuthAttemptInput) {
  trackProductEvent({
    eventName: 'auth.attempt_succeeded',
    route: params.route,
    screen: 'auth',
    component: 'LoginScreen',
    message: `${params.provider} ${params.intent} succeeded`,
    metadata: {
      intent: params.intent,
      provider: params.provider,
      flow: params.flow,
      ...(params.metadata ?? {}),
    },
  });
}

export function trackAuthUserCreated(params: AuthUserCreatedInput) {
  trackProductEvent({
    eventName: 'auth.user_created',
    route: params.route,
    screen: 'auth',
    component: 'LoginScreen',
    message: `${params.provider} created a new auth user`,
    metadata: {
      attemptedIntent: params.attemptedIntent,
      provider: params.provider,
      flow: params.flow,
      supabaseUserId: params.supabaseUserId ?? null,
      ...(params.metadata ?? {}),
    },
  });
}

export function trackAuthAttemptFailed(
  params: AuthAttemptInput & {
    failureStage: 'validation' | 'provider_start' | 'callback' | 'session_exchange' | 'provider';
    errorBucket?: string | null;
    errorMessage?: string | null;
  },
) {
  trackProductEvent({
    eventName: 'auth.attempt_failed',
    route: params.route,
    screen: 'auth',
    component: 'LoginScreen',
    message: `${params.provider} ${params.intent} failed`,
    metadata: {
      intent: params.intent,
      provider: params.provider,
      flow: params.flow,
      failureStage: params.failureStage,
      errorBucket: params.errorBucket ?? null,
      errorMessage: params.errorMessage ?? null,
      ...(params.metadata ?? {}),
    },
  });
}

export function trackAuthSignedOut(params: {
  route: string | null | undefined;
  source: string;
  metadata?: Record<string, AnalyticsScalar> | null;
}) {
  trackProductEvent({
    eventName: 'auth.signed_out',
    route: params.route,
    screen: 'auth',
    component: 'AuthSession',
    message: `Signed out from ${params.source}`,
    metadata: {
      source: params.source,
      ...(params.metadata ?? {}),
    },
  });
}

export function trackMarketingPageViewed(params: {
  route: string | null | undefined;
  pageType: string;
  pageSlug: string;
  articleSlug?: string | null;
  title: string;
  metadata?: Record<string, AnalyticsScalar> | null;
}) {
  trackProductEvent({
    eventName: 'marketing.page_viewed',
    route: params.route,
    screen: 'marketing',
    component: 'MarketingPage',
    message: params.title,
    metadata: {
      pageType: params.pageType,
      pageSlug: params.pageSlug,
      articleSlug: params.articleSlug ?? null,
      ...(params.metadata ?? {}),
    },
  });
}

export function trackMarketingCtaClicked(params: {
  route: string | null | undefined;
  pageType: string;
  pageSlug: string;
  ctaId: string;
  destination: string;
  placement: string;
  articleSlug?: string | null;
  metadata?: Record<string, AnalyticsScalar> | null;
}) {
  trackProductEvent({
    eventName: 'marketing.cta_clicked',
    route: params.route,
    screen: 'marketing',
    component: 'MarketingCTA',
    message: params.ctaId,
    metadata: {
      pageType: params.pageType,
      pageSlug: params.pageSlug,
      articleSlug: params.articleSlug ?? null,
      ctaId: params.ctaId,
      destination: params.destination,
      placement: params.placement,
      ...(params.metadata ?? {}),
    },
  });
}

export function trackSupportPageViewed(params: {
  route: string | null | undefined;
  metadata?: Record<string, AnalyticsScalar> | null;
}) {
  trackProductEvent({
    eventName: 'support.page_viewed',
    route: params.route,
    screen: 'support',
    component: 'SupportPage',
    message: 'Support page viewed',
    metadata: {
      surface: 'public_page',
      ...(params.metadata ?? {}),
    },
  });
}

export function trackSupportModalOpened(params: {
  route: string | null | undefined;
  metadata?: Record<string, AnalyticsScalar> | null;
}) {
  trackProductEvent({
    eventName: 'support.modal_opened',
    route: params.route,
    screen: 'support',
    component: 'HelpSupportModal',
    message: 'Support modal opened',
    metadata: {
      surface: 'in_app_modal',
      ...(params.metadata ?? {}),
    },
  });
}

export function trackConnectionLinkEvent(params: {
  route: string | null | undefined;
  screen?: string | null;
  provider: 'plaid' | 'snaptrade' | 'teller';
  status: 'started' | 'opened' | 'succeeded' | 'failed' | 'exited' | 'closed' | 'event';
  metadata?: Record<string, AnalyticsScalar> | null;
}) {
  trackProductEvent({
    eventName: `connections.link_${params.status}`,
    route: params.route,
    screen: params.screen ?? 'connections',
    component: 'ConnectionFlow',
    message: `${params.provider} link ${params.status}`,
    metadata: {
      provider: params.provider,
      ...(params.metadata ?? {}),
    },
  });
}

export function trackNavigationChanged(params: {
  route: string | null | undefined;
  previousRoute?: string | null;
  currentGroup?: string | null;
  authState: 'anonymous' | 'authenticated';
  metadata?: Record<string, AnalyticsScalar> | null;
}) {
  trackProductEvent({
    eventName: 'navigation.changed',
    route: params.route,
    screen: 'navigation',
    component: 'Router',
    message: params.route ?? 'unknown route',
    metadata: {
      previousRoute: params.previousRoute ?? null,
      currentGroup: params.currentGroup ?? 'root',
      authState: params.authState,
      ...(params.metadata ?? {}),
    },
  });
}

export function trackFireCalculationRequested(input: FireCalculationBaseInput) {
  trackProductEvent({
    eventName: 'fire.calculation_requested',
    route: input.route,
    screen: input.screen,
    component: 'FireCalculation',
    message: 'Requested FIRE calculation',
    metadata: {
      source: input.source,
      transport: input.transport,
      ...(input.metadata ?? {}),
    },
  });
}

export function trackFireCalculationStatusChanged(
  input: FireCalculationStageInput,
) {
  trackProductEvent({
    eventName: 'fire.calculation_status_changed',
    route: input.route,
    screen: input.screen,
    component: 'FireCalculation',
    message: input.status,
    metadata: {
      source: input.source,
      transport: input.transport,
      status: input.status,
      progressPct:
        typeof input.progress === 'number'
          ? Math.round(Math.max(0, Math.min(1, input.progress)) * 100)
          : null,
      ...(input.metadata ?? {}),
    },
  });
}

export function trackFireCalculationCompleted(input: FireCalculationOutcomeInput) {
  trackProductEvent({
    eventName: 'fire.calculation_completed',
    route: input.route,
    screen: input.screen,
    component: 'FireCalculation',
    message: 'FIRE calculation completed',
    metadata: {
      source: input.source,
      transport: input.transport,
      durationMs: input.durationMs ?? null,
      fiYear: input.fiYear ?? null,
      fiAge: input.fiAge ?? null,
      ...(input.metadata ?? {}),
    },
  });
}

export function trackFireCalculationFailed(input: FireCalculationOutcomeInput) {
  trackProductEvent({
    eventName: 'fire.calculation_failed',
    route: input.route,
    screen: input.screen,
    component: 'FireCalculation',
    message: input.errorMessage ?? 'FIRE calculation failed',
    metadata: {
      source: input.source,
      transport: input.transport,
      durationMs: input.durationMs ?? null,
      errorMessage: input.errorMessage ?? null,
      ...(input.metadata ?? {}),
    },
  });
}
